import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import type { DashboardDetail as Detail, DashboardFilters, DashboardWidgetRow } from "@astrum/shared";
import { Button, Checkbox, EmptyState, Icon, Input, Modal, PageBody, Select, SkeletonText, useToast } from "@astrum/ui";
import { DashboardWidgetView } from "../components/DashboardWidgetView.js";
import { ShareDialog, WidgetDialog } from "../components/DashboardDialogs.js";
import { api, ApiError } from "../lib/api.js";
import { sameFilters } from "../lib/dashboards.js";
import { usePageTitle } from "../lib/usePageTitle.js";
import { useWorkspace } from "../lib/workspace.js";
import "./dashboards.css";

type Option = { id: string; label: string };

export function DashboardDetail() {
  const { dashboardId = "" } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { active } = useWorkspace();
  const [dashboard, setDashboard] = useState<Detail | null>(null);
  const [state, setState] = useState<"loading" | "ok" | "missing" | "failed">("loading");
  const [filters, setFilters] = useState<DashboardFilters>({});
  const [teams, setTeams] = useState<Option[]>([]);
  const [people, setPeople] = useState<Option[]>([]);
  const [projects, setProjects] = useState<Option[]>([]);
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<DashboardWidgetRow | null>(null);
  const [sharing, setSharing] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState("");
  usePageTitle(dashboard?.name ?? "Dashboard");

  const load = useCallback(async () => {
    try {
      const res = await api.get<{ dashboard: Detail }>(`/dashboards/${dashboardId}`);
      setDashboard(res.dashboard);
      setState("ok");
      return res.dashboard;
    } catch (error) {
      setState(error instanceof ApiError && error.status === 404 ? "missing" : "failed");
      return null;
    }
  }, [dashboardId]);

  useEffect(() => {
    setState("loading");
    void load().then((d) => d && setFilters(d.filters));
  }, [load]);

  useEffect(() => {
    void api.get<{ teams: { id: string; name: string }[] }>("/teams?limit=200").then((r) => setTeams(r.teams.map((t) => ({ id: t.id, label: t.name })))).catch(() => setTeams([]));
    void api.get<{ people: { id: string; displayName: string }[] }>("/people").then((r) => setPeople(r.people.map((p) => ({ id: p.id, label: p.displayName })))).catch(() => setPeople([]));
    void api
      .get<{ projects: { id: string; code: string; name: string }[] }>("/projects")
      .then((r) => setProjects(r.projects.map((p) => ({ id: p.id, label: `${p.code} · ${p.name}` }))))
      .catch(() => setProjects([]));
  }, []);

  const fail = (error: unknown, fallback: string) => toast(error instanceof ApiError ? error.message : fallback, { tone: "danger" });

  const reload = () => void load();
  const patchWidget = (id: string, body: unknown) => api.patch(`/dashboards/${dashboardId}/widgets/${id}`, body).then(reload).catch((e) => fail(e, "Could not change the widget."));

  const move = (index: number, direction: -1 | 1) => {
    if (!dashboard) return;
    const ids = dashboard.widgets.map((w) => w.id);
    const target = index + direction;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target]!, ids[index]!];
    void api.put(`/dashboards/${dashboardId}/widgets/order`, { widgetIds: ids }).then(reload).catch((e) => fail(e, "Could not reorder."));
  };

  const remove = (id: string) =>
    void api.delete(`/dashboards/${dashboardId}/widgets/${id}`).then(reload).catch((e) => fail(e, "Could not remove the widget."));

  const setFilter = (patch: Partial<DashboardFilters>) =>
    setFilters((f) => {
      const next = { ...f, ...patch };
      for (const key of Object.keys(next) as (keyof DashboardFilters)[]) {
        const v = next[key];
        if (v === undefined || v === "" || (Array.isArray(v) && v.length === 0)) delete next[key];
      }
      return next;
    });

  if (state === "loading") return <PageBody title="Dashboard" width="wide"><SkeletonText lines={5} /></PageBody>;
  if (state !== "ok" || !dashboard) {
    return (
      <PageBody title="Dashboard" width="wide">
        <EmptyState
          icon="insights"
          title={state === "missing" ? "Dashboard not found" : "Could not load the dashboard"}
          description={state === "missing" ? "It may have been deleted or not shared with you." : "Check your connection and try again."}
          action={<Link to="/dashboards">All dashboards</Link>}
        />
      </PageBody>
    );
  }

  const dirty = !sameFilters(filters, dashboard.filters);
  const selectedProjects = filters.projectIds ?? [];

  return (
    <PageBody
      title={dashboard.name}
      subtitle={dashboard.description ?? (dashboard.mine ? undefined : `Shared by ${dashboard.ownerName ?? "a colleague"}`)}
      width="wide"
      actions={
        <div className="dash-noprint" style={{ display: "flex", gap: "var(--sp-2)" }}>
          <Button size="sm" variant="secondary" icon={<Icon name="download" size={13} />} onClick={() => window.print()}>
            Print or PDF
          </Button>
          {dashboard.canEdit && (
            <>
              <Button size="sm" variant="secondary" onClick={() => { setName(dashboard.name); setRenaming(true); }}>
                Rename
              </Button>
              <Button size="sm" variant="secondary" icon={<Icon name="people" size={13} />} onClick={() => setSharing(true)}>
                Share
              </Button>
              <Button size="sm" variant="secondary" icon={<Icon name="trash" size={13} />} onClick={() => void remove_dashboard()}>
                Delete
              </Button>
              <Button size="sm" icon={<Icon name="plus" size={13} />} onClick={() => setAdding(true)}>
                Add widget
              </Button>
            </>
          )}
        </div>
      }
    >
      <p className="dash-noprint">
        <Link to="/dashboards">All dashboards</Link>
      </p>

      <div className="dash-filters" role="group" aria-label="Dashboard filters">
        <span className="dash-filters__office">Office: {active?.name ?? "this office"}</span>
        <Select label="Team" value={filters.teamId ?? ""} onChange={(e) => setFilter({ teamId: e.target.value || undefined })}>
          <option value="">All teams</option>
          {teams.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </Select>
        <div className="dash-filters__projects">
          <Button variant="secondary" size="md" onClick={() => setProjectsOpen((v) => !v)} aria-expanded={projectsOpen}>
            {selectedProjects.length ? `${selectedProjects.length} project${selectedProjects.length === 1 ? "" : "s"}` : "All projects"}
          </Button>
          {projectsOpen && (
            <div className="dash-filters__popover" role="group" aria-label="Projects">
              {projects.map((p) => (
                <Checkbox
                  key={p.id}
                  label={p.label}
                  checked={selectedProjects.includes(p.id)}
                  onChange={() =>
                    setFilter({
                      projectIds: selectedProjects.includes(p.id) ? selectedProjects.filter((x) => x !== p.id) : [...selectedProjects, p.id].slice(0, 20),
                    })
                  }
                />
              ))}
              {projects.length === 0 && <span className="dash-widget__note">No projects.</span>}
            </div>
          )}
        </div>
        <Input label="From" type="date" value={filters.from ?? ""} onChange={(e) => setFilter({ from: e.target.value || undefined })} />
        <Input label="To" type="date" value={filters.to ?? ""} onChange={(e) => setFilter({ to: e.target.value || undefined })} />
        <Select label="Assignee" value={filters.assigneeId ?? ""} onChange={(e) => setFilter({ assigneeId: e.target.value || undefined })}>
          <option value="">Anyone</option>
          {people.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </Select>
        <span className="dash-filters__spacer" />
        {dirty && (
          <div className="dash-noprint" style={{ display: "flex", gap: "var(--sp-2)" }}>
            <Button variant="ghost" size="sm" onClick={() => setFilters(dashboard.filters)}>
              Reset
            </Button>
            {dashboard.canEdit && (
              <Button variant="secondary" size="sm" onClick={() => void api.patch(`/dashboards/${dashboardId}`, { filters }).then(reload).catch((e) => fail(e, "Could not save the filters."))}>
                Save as default
              </Button>
            )}
          </div>
        )}
      </div>

      {dashboard.widgets.length === 0 ? (
        <EmptyState
          icon="insights"
          title="No widgets yet"
          description={dashboard.canEdit ? "Add numbers, charts and lists to this dashboard." : "The owner has not added anything yet."}
        />
      ) : (
        <div className="dash-grid">
          {dashboard.widgets.map((w, i) => (
            <DashboardWidgetView
              key={w.id}
              dashboardId={dashboard.id}
              widget={w}
              filters={filters}
              canEdit={dashboard.canEdit}
              first={i === 0}
              last={i === dashboard.widgets.length - 1}
              onEdit={() => setEditing(w)}
              onResize={(size) => void patchWidget(w.id, { size })}
              onMove={(d) => move(i, d)}
              onRemove={() => remove(w.id)}
            />
          ))}
        </div>
      )}

      <WidgetDialog
        open={adding || editing !== null}
        dashboardId={dashboard.id}
        widget={editing}
        onClose={() => {
          setAdding(false);
          setEditing(null);
        }}
        onSaved={() => {
          setAdding(false);
          setEditing(null);
          reload();
        }}
      />
      {dashboard.canEdit && (
        <ShareDialog
          open={sharing}
          dashboard={dashboard}
          onClose={() => setSharing(false)}
          onSaved={() => {
            setSharing(false);
            reload();
          }}
        />
      )}
      <Modal open={renaming} onClose={() => setRenaming(false)} title="Rename dashboard">
        <form
          className="dash-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim()) return;
            void api
              .patch(`/dashboards/${dashboardId}`, { name: name.trim() })
              .then(() => {
                setRenaming(false);
                reload();
              })
              .catch((err) => fail(err, "Could not rename."));
          }}
        >
          <Input label="Name" value={name} maxLength={100} autoFocus onChange={(e) => setName(e.target.value)} />
          <div className="dash-form__actions">
            <Button variant="ghost" type="button" onClick={() => setRenaming(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim()}>
              Save
            </Button>
          </div>
        </form>
      </Modal>
    </PageBody>
  );

  async function remove_dashboard() {
    if (!window.confirm(`Delete "${dashboard!.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/dashboards/${dashboardId}`);
      navigate("/dashboards");
    } catch (error) {
      fail(error, "Could not delete the dashboard.");
    }
  }
}
